import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { reportAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

const Reports: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState<'daily' | 'weekly' | 'monthly' | 'regular-irregular'>('regular-irregular');
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [reportData, setReportData] = useState<any>(null);
  const [regularIrregularData, setRegularIrregularData] = useState<any>(null);

  const buildApiBase = () => (typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api');
  const handleDownload = () => {
    if (!selectedDate) return;
    const base = buildApiBase();
    const dateStr = selectedDate.toISOString().split('T')[0];
    if (reportType === 'daily') {
      const url = `${base}/admin/reports/daily.csv?date=${encodeURIComponent(dateStr)}`;
      window.location.href = url;
    }
  };

  const loadReport = async () => {
    if (!selectedDate) return;

    try {
      setLoading(true);
      setError('');
      
      let data;
      switch (reportType) {
        case 'daily':
          data = await reportAPI.getDailyReport(selectedDate.toISOString().split('T')[0]);
          setReportData(data);
          setRegularIrregularData(null);
          break;
        case 'weekly':
          data = await reportAPI.getWeeklyReport(selectedDate.toISOString().split('T')[0]);
          setReportData(data);
          setRegularIrregularData(null);
          break;
        case 'monthly':
          data = await reportAPI.getMonthlyReport(selectedDate.toISOString().split('T')[0]);
          setReportData(data);
          setRegularIrregularData(null);
          break;
        case 'regular-irregular':
          data = await reportAPI.getRegularIrregularReport(selectedDate.toISOString().split('T')[0]);
          setRegularIrregularData(data);
          setReportData(null);
          break;
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nel caricamento del report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadReport();
    }
  }, [reportType, selectedDate, user]);

  if (!isAdmin) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Report
        </Typography>
        <Alert severity="info">
          Accesso negato. Questa sezione appartiene all'area amministrativa dei report.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Report e Statistiche
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtri Report
        </Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={2}>
          <TextField
            select
            label="Tipo Report"
            value={reportType}
            onChange={(e) => setReportType(e.target.value as any)}
            inputProps={{ 'aria-label': 'Tipo Report' }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="regular-irregular">Regolari/Irregolari</MenuItem>
            <MenuItem value="daily">Giornaliero</MenuItem>
            <MenuItem value="weekly">Settimanale</MenuItem>
            <MenuItem value="monthly">Mensile</MenuItem>
          </TextField>
          
          <TextField
            label="Data"
            type="date"
            value={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          
          <Button
            variant="contained"
            onClick={loadReport}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Carica Report'}
          </Button>

          {reportType === 'daily' && (
            <Button
              variant="outlined"
              onClick={handleDownload}
              disabled={loading || !selectedDate}
            >
              Scarica CSV (Giornaliero)
            </Button>
          )}
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : regularIrregularData ? (
        <Box>
          {/* Report Regolari/Irregolari */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              Report Regolari/Irregolari - {regularIrregularData?.date || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Tolleranza quadratura: ±{regularIrregularData?.tolerance_minutes || 10} minuti
            </Typography>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card sx={{ borderLeft: '4px solid #2196f3' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Totale</Typography>
                    <Typography variant="h4">{regularIrregularData?.summary?.total || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ borderLeft: '4px solid #4caf50' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Regolari</Typography>
                    <Typography variant="h4" sx={{ color: '#4caf50' }}>
                      {regularIrregularData?.summary?.regular || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ borderLeft: '4px solid #f44336' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Irregolari</Typography>
                    <Typography variant="h4" sx={{ color: '#f44336' }}>
                      {regularIrregularData?.summary?.irregular || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Con Quadratura</Typography>
                    <Typography variant="h4" sx={{ color: '#ff9800' }}>
                      {regularIrregularData?.summary?.with_quadratura || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Elenco Regolari */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" />
                Situazioni Regolari ({regularIrregularData?.regular?.length || 0})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Dipendente</strong></TableCell>
                      <TableCell><strong>Matricola</strong></TableCell>
                      <TableCell><strong>Dipartimento</strong></TableCell>
                      <TableCell align="right"><strong>Ore Previste</strong></TableCell>
                      <TableCell align="right"><strong>Ore Effettive</strong></TableCell>
                      <TableCell align="right"><strong>Differenza</strong></TableCell>
                      <TableCell align="center"><strong>Quadratura</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!regularIrregularData?.regular || regularIrregularData.regular.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary">Nessuna situazione regolare</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      regularIrregularData.regular.map((emp: any) => (
                        <TableRow key={emp.employee_id} hover>
                          <TableCell>{emp.nome || ''} {emp.cognome || ''}</TableCell>
                          <TableCell>{emp.matricola || 'N/A'}</TableCell>
                          <TableCell>{emp.dipartimento || 'N/A'}</TableCell>
                          <TableCell align="right">{emp.ore_previste ? emp.ore_previste.toFixed(2) : '0.00'}h</TableCell>
                          <TableCell align="right">{emp.ore_effettive ? emp.ore_effettive.toFixed(2) : '0.00'}h</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${(emp.differenza || 0) >= 0 ? '+' : ''}${emp.differenza_minuti || 0} min`}
                              size="small"
                              color={(emp.differenza || 0) === 0 ? 'default' : 'info'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {emp.quadratura ? (
                              <Chip
                                label="±10 min"
                                size="small"
                                color="warning"
                                icon={<WarningIcon />}
                              />
                            ) : (
                              <Chip
                                label="Esatto"
                                size="small"
                                color="success"
                                icon={<CheckCircleIcon />}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Elenco Irregolari */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="error" />
                Situazioni Irregolari ({regularIrregularData?.irregular?.length || 0})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Dipendente</strong></TableCell>
                      <TableCell><strong>Matricola</strong></TableCell>
                      <TableCell><strong>Dipartimento</strong></TableCell>
                      <TableCell align="right"><strong>Ore Previste</strong></TableCell>
                      <TableCell align="right"><strong>Ore Effettive</strong></TableCell>
                      <TableCell align="right"><strong>Differenza</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!regularIrregularData?.irregular || regularIrregularData.irregular.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary">Nessuna situazione irregolare</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      regularIrregularData.irregular.map((emp: any) => (
                        <TableRow key={emp.employee_id} hover sx={{ backgroundColor: '#ffebee' }}>
                          <TableCell><strong>{emp.nome || ''} {emp.cognome || ''}</strong></TableCell>
                          <TableCell>{emp.matricola || 'N/A'}</TableCell>
                          <TableCell>{emp.dipartimento || 'N/A'}</TableCell>
                          <TableCell align="right">{emp.ore_previste ? emp.ore_previste.toFixed(2) : '0.00'}h</TableCell>
                          <TableCell align="right">{emp.ore_effettive ? emp.ore_effettive.toFixed(2) : '0.00'}h</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${(emp.differenza || 0) >= 0 ? '+' : ''}${emp.differenza_minuti || 0} min`}
                              size="small"
                              color={(emp.differenza || 0) > 0 ? 'error' : 'warning'}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Box>
      ) : reportData ? (
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <PeopleIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Dipendenti Totali
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {reportData.report?.total_employees || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Check-in
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {reportData.report?.checked_in || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Check-out
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {reportData.report?.checked_out || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Ore Totali
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {reportData.report?.total_hours || 0}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {reportData.report?.average_hours && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Statistiche Aggiuntive
                </Typography>
                <Typography variant="body1">
                  Ore medie per dipendente: {reportData.report.average_hours}h
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary" align="center">
            Seleziona un periodo e carica un report per visualizzare le statistiche.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default Reports;
